import { EnvType } from "../services";
import ApiClient from "../api-client";
import IJobBrokerApiClient from "./job-broker-api-client";
import JobBrokerApiClient from "./job-broker-api-client";
import { GetJobsResponse } from "../Types/get-jobs-response";
import { GetJobResponse } from "../Types/get-job-response";
import { GetRulesResponse } from "../Types/get-rules-response";
import { GetRuleResponse } from "../Types/get-rule-response";
import { GenericResponseStd } from "../Types/generic-response-std";
import { ApplyRuleOnObjectsResponse } from "../Types/apply-rule-on-objects-response";
import { ApplyRuleOnObjectsRequest } from "../Types/apply-rule-on-objects-request";
import { DbJobRuleInfos } from "../Types/db-job-rule-infos";
import { JobTypes } from "../Types/job-types";
import { GetRulesMatchingObjectRequest } from "../Types/get-rules-request";

export interface DownloadJobResult {
  data: string;
  fileName: string;
}

export default class JobBrokerService {
  jobBrokerApiClient: IJobBrokerApiClient;

  constructor(envType: EnvType) {
    this.jobBrokerApiClient = new JobBrokerApiClient(new ApiClient(envType), envType);
  }

  async ListJobsWithPage(pageNb: number, pageSize: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.ListJobsWithPage(pageNb, pageSize);
  }
  async ListJobsWithType(type: JobTypes): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.ListJobsWithType(type);
  }
  async ListJobsWithTypeAndPage(type: JobTypes, pageNb: number, pageSize: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.ListJobsWithTypeAndPage(type, pageNb, pageSize);
  }
  async ListJobsWithRuleId(ruleId: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.ListJobsWithRuleId(ruleId);
  }
  async ListJobsWithRuleIdAndPage(ruleId: number, pageNb: number, pageSize: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.ListJobsWithRuleIdAndPage(ruleId, pageNb, pageSize);
  }
  async GetJob(id: number): Promise<GetJobResponse> {
    return await this.jobBrokerApiClient.GetJob(id);
  }
  async ListByObjectId(objectId: number): Promise<GetJobsResponse> {
    return await this.jobBrokerApiClient.ListByObjectId(objectId);
  }
  async SetPriorityOnJobItemById(id: number, priority: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.SetPriorityOnJobItemById(id, priority);
  }
  async Retry(id: number, executeNow: boolean): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.Retry(id, executeNow);
  }
  async Cancel(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.Cancel(id);
  }
  async Abort(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.Abort(id);
  }
  async NotifyResync(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.NotifyResync(id);
  }
  async BypassJobResult(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.BypassJobResult(id);
  }
  async DownloadJobResult(id: number): Promise<DownloadJobResult> {
    const [fileData, headers] = await this.jobBrokerApiClient.DownloadJobResult(id);

    const headerContentDisp = headers["content-disposition"];
    let fileName = `JobResultDownload${id}.pdf`;
    const headerStartsWith = "attachment; filename=";
    if (headerContentDisp && headerContentDisp.includes(headerStartsWith)) {
      const split = headerContentDisp.substring(headerStartsWith.length).split(";");
      fileName = split[0];
    }

    const blob = new Blob([fileData], { type: "application/pdf" });
    const data = window.URL.createObjectURL(blob);
    return { data, fileName };
  }

  async ListRules(): Promise<GetRulesResponse> {
    return await this.jobBrokerApiClient.ListRules();
  }
  async GetRule(id: number): Promise<GetRuleResponse> {
    return await this.jobBrokerApiClient.GetRule(id);
  }
  async ListRulesByObject(objectId: number): Promise<GetRulesResponse> {
    return await this.jobBrokerApiClient.ListRulesByObject(objectId);
  }
  async ListRulesByObjects(request: GetRulesMatchingObjectRequest): Promise<GetRulesResponse> {
    return await this.jobBrokerApiClient.ListRulesByObjects(request);
  }
  async CreateRule(request: DbJobRuleInfos): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.CreateRule(request);
  }
  async UpdateRule(request: DbJobRuleInfos): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.UpdateRule(request);
  }
  async DeleteRule(id: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.DeleteRule(id);
  }
  async ApplyRuleOnObject(ruleId: number, objectId: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.ApplyRuleOnObject(ruleId, objectId);
  }
  async ApplyRuleOnObjects(request: ApplyRuleOnObjectsRequest): Promise<ApplyRuleOnObjectsResponse> {
    return await this.jobBrokerApiClient.ApplyRuleOnObjects(request);
  }
  async ApplyRuleOnObjectAndFile(ruleId: number, objectId: number, fileId: number): Promise<GenericResponseStd> {
    return await this.jobBrokerApiClient.ApplyRuleOnObjectAndFile(ruleId, objectId, fileId);
  }

  //------Utils------
  GetServiceUrl(): string {
    return this.jobBrokerApiClient.GetApiUrl();
  }
}
